import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// UUID v4 generator - a proper RFC4122 version 4 compliant UUID generator
export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0,
        v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Format milliseconds to HH:MM:SS
export function formatTime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// Format timestamp to human-readable time
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatDateToISO(date: Date): string {
  // Always use local date components rather than toISOString() which uses UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  const formattedDate = `${year}-${month}-${day}`;
  console.log('formatDateToISO:', formattedDate, 'from date:', date);
  
  return formattedDate;
}

// Format date for display with option for short format (for mobile)
export function formatDateForDisplay(dateStr: string, shortFormat: boolean = false): string {
  const date = new Date(dateStr);
  
  if (shortFormat) {
    // Short format (e.g. "Mon, Jan 15")
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
  }
  
  // Full format (e.g. "Mon, Jan 15, 2025")
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
}

// Generate a unique ID
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

// Format minutes to HH:MM
export function formatMinutesToHHMM(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

// Enhanced event bus for app-wide synchronization with debounce capabilities
interface EventCallback {
  (data: any): void;
  _originalCallback?: Function;
}

const callbacks: Record<string, EventCallback[]> = {};
const debounceTimers: Record<string, number> = {};

// Create a custom event bus for app-wide synchronization
const eventBus = {
  on(event: string, callback: Function, debounceMs: number = 0) {
    if (!callbacks[event]) {
      callbacks[event] = [];
    }
    
    const wrappedCallback: EventCallback = (data: any) => {
      callback(data);
    };
    
    // Store the original callback for reference (needed for removal)
    wrappedCallback._originalCallback = callback;
    
    callbacks[event].push(wrappedCallback);
    
    // Return an "off" function for easy cleanup
    return () => this.remove(event, callback);
  },
  
  // Add an off method to explicitly unsubscribe from events
  off(event: string, callback: Function) {
    this.remove(event, callback);
  },
  
  dispatch(event: string, data?: any, debounceMs: number = 0) {
    console.log(`Event dispatched: ${event}`, data);
    
    if (debounceMs > 0) {
      if (debounceTimers[event]) {
        clearTimeout(debounceTimers[event]);
      }
      
      debounceTimers[event] = window.setTimeout(() => {
        this._executeCallbacks(event, data);
      }, debounceMs);
    } else {
      this._executeCallbacks(event, data);
    }
  },
  
  _executeCallbacks(event: string, data: any) {
    if (callbacks[event]) {
      callbacks[event].forEach(callback => callback(data));
    }
    
    // Also execute global listeners (prefixed with '*')
    if (callbacks['*']) {
      callbacks['*'].forEach(callback => callback({ type: event, data }));
    }
  },
  
  remove(event: string, callbackToRemove: Function) {
    if (callbacks[event]) {
      callbacks[event] = callbacks[event].filter(
        callback => callback._originalCallback !== callbackToRemove
      );
      
      if (callbacks[event].length === 0) {
        delete callbacks[event];
      }
    }
  },
  
  clearAll() {
    Object.keys(callbacks).forEach(event => {
      delete callbacks[event];
    });
    
    Object.keys(debounceTimers).forEach(event => {
      clearTimeout(debounceTimers[event]);
      delete debounceTimers[event];
    });
  }
};

export { eventBus };

// Define commonly used event types for data synchronization
export const SYNC_EVENTS = {
  FOOD_UPDATED: 'wellness-food-updated',
  WALKING_UPDATED: 'wellness-walking-updated',
  FASTING_UPDATED: 'wellness-fasting-updated',
  HEALTH_UPDATED: 'wellness-health-updated',
  JOURNAL_UPDATED: 'wellness-journal-updated',
  WATER_UPDATED: 'wellness-water-updated',
  ALL_DATA_UPDATED: 'wellness-all-data-updated',
  SYNC_COMPLETED: 'sync_completed',
  SYNC_ERROR: 'sync_error',
  LOCAL_UPDATE: 'local_update'
};
